/**
 * El material de la base de conocimiento que NO es un canal ni un video de las
 * tres plataformas: archivos, páginas web y texto pegado.
 *
 * Vive separado de `canalesReferencia.ts` porque es otra cosa: un canal es una
 * URL de YouTube/Instagram/TikTok que se puede canonizar y deduplicar por su
 * contenido, y acá conviven un archivo del que sólo tenemos el nombre, una
 * página cualquiera de internet y un texto suelto. Comparten, eso sí, el
 * criterio: LÓGICA PURA, sin React ni storage, testeada en
 * `materialesConocimiento.test.ts`.
 *
 * LO QUE PASA CON UN ARCHIVO — leer antes de tocar nada. No hay backend para
 * esto: el archivo NO SE SUBE A NINGÚN LADO. En `localStorage` no entra un
 * archivo, así que lo único que queda registrado son sus datos (nombre, tamaño,
 * tipo) y la interfaz lo dice con todas las letras, tres veces: en el atajo
 * antes de abrir el buscador de archivos, en la pantalla de confirmación antes
 * de anotar nada, y en cada tarjeta de la lista. Si la pantalla dejara creer
 * que el archivo se subió, el usuario pensaría que su material está en Atlas
 * cuando no está en ningún lado — el peor defecto posible de esta pantalla.
 * Por eso el tipo se llama `MaterialArchivo` y no guarda ningún contenido: no
 * hay dónde guardarlo, y un campo vacío esperando el día que lo haya sería una
 * invitación a creer que ya pasa.
 *
 * Ver ADR-2026-08-17-estudio-marketing-modal-anadir-fuente.
 */

import { interpretarUrlFuente } from "./canalesReferencia";
import { hashTexto, idEstable } from "./idEstable";

export type TipoMaterial = "archivo" | "web" | "texto";

/**
 * Un archivo del que SÓLO tenemos los datos. Sin contenido, sin URL, sin
 * `blob`: el archivo se quedó en la computadora del usuario.
 */
export interface MaterialArchivo {
  tipo: "archivo";
  /** Determinista (nombre + tamaño): volver a elegir el mismo archivo dedupe. */
  id: string;
  nombre: string;
  bytes: number;
  /** "PDF", "DOCX", "MP4"… lo que se le puede mostrar al usuario. */
  formato: string;
}

/** Una página web cualquiera. Guardamos el LINK, no la página. */
export interface MaterialWeb {
  tipo: "web";
  id: string;
  /** URL canónica (sin fragmento, host en minúsculas). */
  url: string;
  /** El dominio sin `www.`, para mostrar. */
  sitio: string;
}

/** Texto pegado a mano. Este sí se guarda entero (en el navegador). */
export interface MaterialTexto {
  tipo: "texto";
  id: string;
  titulo: string;
  texto: string;
}

export type MaterialConocimiento = MaterialArchivo | MaterialWeb | MaterialTexto;

/* ── Página web ─────────────────────────────────────────────────────────── */

export type MotivoPaginaWeb =
  | "vacia"
  | "no-es-url"
  /** Parseó, pero el "dominio" no puede serlo (`hola`, `algo.`). */
  | "sin-dominio"
  /** `localhost`, una IP, un `.local`: sólo existe en la máquina del usuario. */
  | "no-publica"
  /** Es de YouTube/Instagram/TikTok: tiene atajo propio y se leería mejor ahí. */
  | "es-una-plataforma";

export type LecturaPaginaWeb =
  | { ok: true; url: string; sitio: string }
  | { ok: false; motivo: MotivoPaginaWeb };

export const MENSAJE_PAGINA_WEB: Record<MotivoPaginaWeb, string> = {
  vacia: "Pegá el link de la página.",
  "no-es-url": "Eso no parece un link. Pegá la dirección completa (empieza con https://).",
  "sin-dominio": "Falta el dominio: una dirección se ve como “ejemplo.com/nota”.",
  "no-publica":
    "Esa dirección sólo existe en tu computadora o en tu red: nadie más la puede abrir.",
  "es-una-plataforma":
    "Ese link es de YouTube, Instagram o TikTok: usá “Extraer conocimiento de video” o “Vincular canal de creador”.",
};

/** Un host que es una IP (v4) o una dirección de red local. Ninguna sirve como
 *  fuente: la página no existe fuera de esa máquina. */
const ES_IPV4 = /^\d{1,3}(?:\.\d{1,3}){3}$/;
/** `ejemplo.com`, `sub.ejemplo.com.ar`: al menos dos etiquetas y un TLD de
 *  letras. Cubre los IDN, que `new URL` ya convirtió a `xn--…`. */
const DOMINIO_VALIDO = /^[a-z0-9-]+(?:\.[a-z0-9-]+)*\.[a-z]{2,}$/;

/**
 * Parámetros que NO cambian de página: los pone quien comparte el link, no el
 * sitio. Un link copiado de Twitter, de Instagram o de un newsletter viene
 * SIEMPRE con alguno, así que sin esta limpieza la misma nota entra dos y tres
 * veces en "Otro material" (los canales y los videos ya estaban cubiertos, pero
 * de rebote: su canónica se rearma desde los segmentos y la query se pierde).
 *
 * Es una lista NEGRA acotada y no "borrar toda la query": `?id=3` y `?p=12`
 * identifican la nota, y tirarlos guardaría el link de otra página.
 */
const PARAMS_SEGUIMIENTO = new Set([
  "fbclid",
  "gclid",
  "dclid",
  "msclkid",
  "yclid",
  "igshid",
  "igsh",
  "mc_cid",
  "mc_eid",
]);

/** La query sin los parámetros de seguimiento, lista para concatenar (con `?`
 *  adelante) o vacía. Todo lo que empieza con `utm_` es de la misma familia. */
function sinParametrosDeSeguimiento(url: URL): string {
  const params = new URLSearchParams(url.search);
  for (const clave of [...params.keys()]) {
    const normalizada = clave.toLowerCase();
    if (normalizada.startsWith("utm_") || PARAMS_SEGUIMIENTO.has(normalizada)) {
      params.delete(clave);
    }
  }
  const texto = params.toString();
  return texto ? `?${texto}` : "";
}

/**
 * El link de una página web cualquiera.
 *
 * Es la validación MÁS PERMISIVA de las cuatro —cualquier dominio de internet
 * vale— y justo por eso necesita los rechazos explícitos: sin ellos, "hola"
 * sería una página (`new URL("https://hola")` parsea sin chistar) y
 * `http://localhost:3000` también. Los dos son cosas que Atlas nunca va a poder
 * leer, y guardarlos sería anotar una fuente muerta.
 */
export function interpretarPaginaWeb(crudo: string): LecturaPaginaWeb {
  const texto = crudo.trim();
  if (!texto) return { ok: false, motivo: "vacia" };

  const esHttp = /^https?:\/\//i.test(texto);
  // Mismo criterio que `interpretarUrlFuente`: se completa el esquema que
  // falta, pero NUNCA se le antepone `https://` a algo que ya trae otro.
  if (/^[a-z][a-z0-9+.-]*:/i.test(texto) && !esHttp) return { ok: false, motivo: "no-es-url" };

  let url: URL;
  try {
    url = new URL(esHttp ? texto : `https://${texto}`);
  } catch {
    return { ok: false, motivo: "no-es-url" };
  }

  const host = url.hostname.toLowerCase();
  if (host === "localhost" || host.endsWith(".local") || host.endsWith(".localhost")) {
    return { ok: false, motivo: "no-publica" };
  }
  if (ES_IPV4.test(host) || host.startsWith("[")) return { ok: false, motivo: "no-publica" };
  if (!DOMINIO_VALIDO.test(host)) return { ok: false, motivo: "sin-dominio" };
  // Las tres plataformas tienen atajo propio, que además las canoniza y las
  // deduplica contra los canales ya cargados.
  if (interpretarUrlFuente(texto).ok) return { ok: false, motivo: "es-una-plataforma" };

  // Canónica: el fragmento (`#seccion`) no cambia de página y haría entrar dos
  // veces la misma; el puerto por defecto ya lo saca `URL`; los parámetros de
  // seguimiento tampoco cambian de página (ver `PARAMS_SEGUIMIENTO`).
  const busqueda = sinParametrosDeSeguimiento(url);
  const camino = url.pathname === "/" && !busqueda ? "" : `${url.pathname}${busqueda}`;
  return {
    ok: true,
    url: `${url.protocol}//${url.host.toLowerCase()}${camino}`,
    sitio: host.replace(/^www\./, ""),
  };
}

/* ── Texto pegado ───────────────────────────────────────────────────────── */

/** Menos que esto no es conocimiento: es un título suelto o un pegado fallido. */
export const MIN_TEXTO = 20;
/**
 * El techo. No es un capricho de producto: el texto se guarda en
 * `localStorage`, que tiene ~5 MB para TODA la app, y un pegado desmedido
 * (alguien copia un libro entero) haría fallar la escritura y se llevaría
 * puesto lo que ya estaba guardado.
 */
export const MAX_TEXTO = 20000;

export type MotivoTextoPegado = "vacio" | "muy-corto" | "muy-largo";

export type LecturaTextoPegado =
  | { ok: true; texto: string; caracteres: number }
  | { ok: false; motivo: MotivoTextoPegado; caracteres: number };

export const MENSAJE_TEXTO_PEGADO: Record<MotivoTextoPegado, string> = {
  vacio: "Pegá el texto que querés que Atlas tenga en cuenta.",
  "muy-corto": `Muy corto: pegá al menos ${MIN_TEXTO} caracteres.`,
  "muy-largo": `Demasiado largo: hasta ${MAX_TEXTO.toLocaleString("es-AR")} caracteres por vez.`,
};

/** Los saltos de línea de Windows cuentan doble y ensucian el conteo. */
const normalizarTexto = (crudo: string) => crudo.replace(/\r\n?/g, "\n").trim();

export function validarTextoPegado(crudo: string): LecturaTextoPegado {
  const texto = normalizarTexto(crudo);
  const caracteres = texto.length;
  if (caracteres === 0) return { ok: false, motivo: "vacio", caracteres };
  if (caracteres < MIN_TEXTO) return { ok: false, motivo: "muy-corto", caracteres };
  if (caracteres > MAX_TEXTO) return { ok: false, motivo: "muy-largo", caracteres };
  return { ok: true, texto, caracteres };
}

/** Cuántos caracteres cuenta el contador de la pantalla: los MISMOS que
 *  valida `validarTextoPegado`, no los del `<textarea>` crudo. Con `\r\n` los
 *  dos números no coinciden y el contador diría "pasado" con un texto que
 *  entra (o al revés). */
export const contarTexto = (crudo: string) => normalizarTexto(crudo).length;

const MAX_TITULO = 60;

/**
 * El título de un texto pegado: el que escribió el usuario o, si no escribió
 * ninguno, la primera línea recortada. Sin esto, la lista mostraría "Texto
 * pegado" cinco veces y no habría forma de saber cuál es cuál.
 */
export function titularTexto(texto: string, propuesto?: string): string {
  const aMano = (propuesto ?? "").trim();
  if (aMano) return aMano.length > MAX_TITULO ? `${aMano.slice(0, MAX_TITULO).trimEnd()}…` : aMano;
  const primeraLinea = normalizarTexto(texto).split("\n").find((linea) => linea.trim()) ?? "";
  const limpia = primeraLinea.trim();
  if (limpia.length <= MAX_TITULO) return limpia || "Texto pegado";
  // Se corta en un espacio para no partir una palabra al medio.
  const recorte = limpia.slice(0, MAX_TITULO);
  const ultimoEspacio = recorte.lastIndexOf(" ");
  return `${(ultimoEspacio > MAX_TITULO / 2 ? recorte.slice(0, ultimoEspacio) : recorte).trimEnd()}…`;
}

/* ── Archivos ───────────────────────────────────────────────────────────── */

export type MotivoArchivo = "sin-nombre" | "vacio";

/** Lo poco que un `File` del navegador puede dejar acá adentro. El módulo es
 *  puro: recibe estos tres datos, no un `File`. */
export interface ArchivoElegido {
  nombre: string;
  bytes: number;
  tipoMime: string;
}

export type LecturaArchivo =
  | { ok: true; material: MaterialArchivo }
  | { ok: false; motivo: MotivoArchivo };

export const MENSAJE_ARCHIVO: Record<MotivoArchivo, string> = {
  "sin-nombre": "No pudimos leer el nombre del archivo.",
  vacio: "El archivo está vacío (0 bytes): no hay nada para leer.",
};

/** `C:\fakepath\informe.pdf` y `carpeta/informe.pdf` son el mismo archivo. */
const soloNombre = (nombre: string) => nombre.trim().split(/[\\/]/).pop() ?? "";

const FORMATO_POR_MIME: Record<string, string> = {
  "application/pdf": "PDF",
  "text/plain": "TXT",
  "application/msword": "DOC",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "DOCX",
};

/** El formato que se le muestra al usuario: la extensión, que es lo que él ve
 *  en su carpeta. El MIME es el plan B (algunos sistemas mandan archivos sin
 *  extensión) y "Archivo" el último recurso. */
function formatoDe(nombre: string, tipoMime: string): string {
  const punto = nombre.lastIndexOf(".");
  const extension = punto > 0 ? nombre.slice(punto + 1) : "";
  if (extension && extension.length <= 5 && /^[a-z0-9]+$/i.test(extension)) {
    return extension.toUpperCase();
  }
  const mime = tipoMime.trim().toLowerCase();
  if (FORMATO_POR_MIME[mime]) return FORMATO_POR_MIME[mime];
  const subtipo = mime.split("/")[1];
  if (subtipo && /^[a-z0-9.+-]+$/.test(subtipo)) return subtipo.split("+")[0].toUpperCase();
  return "Archivo";
}

/**
 * Anota un archivo: nombre, tamaño y formato. NADA MÁS — el archivo se queda
 * en la computadora del usuario (ver el encabezado del módulo).
 */
export function anotarArchivo(elegido: ArchivoElegido): LecturaArchivo {
  const nombre = soloNombre(elegido.nombre);
  if (!nombre) return { ok: false, motivo: "sin-nombre" };
  const bytes = elegido.bytes;
  if (!Number.isFinite(bytes) || bytes <= 0) return { ok: false, motivo: "vacio" };
  const redondeados = Math.round(bytes);
  return {
    ok: true,
    material: {
      tipo: "archivo",
      // Nombre EXACTO + tamaño, no un slug: `informe v2.pdf` e `informe-v2.pdf`
      // del mismo tamaño son dos archivos y compartían id, así que el segundo
      // se rechazaba como "ya estaba" y desaparecía en silencio.
      id: idEstable("mat-archivo", `${nombre}::${redondeados}`),
      nombre,
      bytes: redondeados,
      formato: formatoDe(nombre, elegido.tipoMime),
    },
  };
}

const UNIDADES = ["KB", "MB", "GB", "TB"];

/** "1,5 MB". Con coma decimal, que es como se escribe en castellano. */
export function formatearTamano(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  let valor = bytes / 1024;
  let unidad = 0;
  while (valor >= 1024 && unidad < UNIDADES.length - 1) {
    valor /= 1024;
    unidad += 1;
  }
  // Un decimal hasta 10 (1,5 MB dice algo); de ahí para arriba sobra (137 MB).
  return `${valor.toFixed(valor < 10 ? 1 : 0).replace(".", ",")} ${UNIDADES[unidad]}`;
}

/* ── Armado de los materiales ───────────────────────────────────────────── */

/**
 * El id de una página sale de su URL canónica ENTERA (`idEstable`), no de un
 * slug: con el slug, `ejemplo.com/a-b` y `ejemplo.com/a/b` —dos páginas
 * distintas— daban el mismo id y, como el dedup de materiales es POR ID, la
 * segunda no entraba y la pantalla decía "esa página ya está en la lista".
 * Un falso positivo de dedup es peor que un dedup que no dedupea: la página no
 * se guarda y el mensaje miente.
 */
export const materialWeb = (url: string, sitio: string): MaterialWeb => ({
  tipo: "web",
  id: idEstable("mat-web", url),
  url,
  sitio,
});

export const materialTexto = (texto: string, titulo: string): MaterialTexto => ({
  tipo: "texto",
  id: `mat-texto-${hashTexto(texto)}`,
  titulo,
  texto,
});

export interface ResumenAgregado {
  materiales: MaterialConocimiento[];
  /**
   * Los que ENTRARON de verdad, en orden. No es lo mismo que lo que se le
   * pasó: el aviso nombra un material ("Anotamos “Informe Q3.pdf”") y, si
   * nombrara el primero de lo pedido, con una tanda donde ese primero ya
   * estaba cargado le diría al usuario que anotó justo el que no anotó.
   */
  nuevos: MaterialConocimiento[];
  agregados: number;
  /** Los que ya estaban. Se cuentan aparte para poder decirlo: "3 anotados, 1
   *  ya estaba" es información; "listo" a secas esconde que algo no entró. */
  duplicados: number;
}

/** Agrega uno o varios (los archivos entran de a montones) sin mutar la lista
 *  y sin repetir por id. */
export function agregarMateriales(
  lista: MaterialConocimiento[],
  nuevos: MaterialConocimiento[],
): ResumenAgregado {
  const vistos = new Set(lista.map((m) => m.id));
  const materiales = [...lista];
  const entraron: MaterialConocimiento[] = [];
  let duplicados = 0;
  for (const material of nuevos) {
    if (vistos.has(material.id)) {
      duplicados += 1;
      continue;
    }
    vistos.add(material.id);
    materiales.push(material);
    entraron.push(material);
  }
  return { materiales, nuevos: entraron, agregados: entraron.length, duplicados };
}

export const quitarMaterial = (
  lista: MaterialConocimiento[],
  id: string,
): MaterialConocimiento[] => lista.filter((m) => m.id !== id);

/* ── Lo guardado en el navegador ────────────────────────────────────────── */

/**
 * Interpreta lo guardado. BLINDADA con el mismo criterio que
 * `leerCanalesGuardados`: cualquier cosa que no sea la forma esperada se
 * descarta en vez de tumbar la pantalla, y los campos DERIVADOS (el formato, el
 * sitio, el id) se vuelven a calcular en vez de creerle al JSON — que es
 * editable desde el DevTools. El texto se vuelve a validar entero: si alguien
 * mete a mano un texto de 5 MB, la próxima escritura reventaría el storage.
 */
export function leerMaterialesGuardados(crudo: string | null): MaterialConocimiento[] {
  if (!crudo) return [];
  let dato: unknown;
  try {
    dato = JSON.parse(crudo);
  } catch {
    return [];
  }
  if (!Array.isArray(dato)) return [];
  const leidos: MaterialConocimiento[] = [];
  for (const item of dato) {
    if (typeof item !== "object" || item === null) continue;
    const guardado = item as Record<string, unknown>;
    if (guardado.tipo === "archivo") {
      if (typeof guardado.nombre !== "string" || typeof guardado.bytes !== "number") continue;
      const leido = anotarArchivo({
        nombre: guardado.nombre,
        bytes: guardado.bytes,
        tipoMime: typeof guardado.formato === "string" ? `x/${guardado.formato}` : "",
      });
      if (leido.ok) leidos.push(leido.material);
      continue;
    }
    if (guardado.tipo === "web") {
      if (typeof guardado.url !== "string") continue;
      const leida = interpretarPaginaWeb(guardado.url);
      if (leida.ok) leidos.push(materialWeb(leida.url, leida.sitio));
      continue;
    }
    if (guardado.tipo === "texto") {
      if (typeof guardado.texto !== "string") continue;
      const leido = validarTextoPegado(guardado.texto);
      if (!leido.ok) continue;
      const titulo = typeof guardado.titulo === "string" ? guardado.titulo : "";
      leidos.push(materialTexto(leido.texto, titularTexto(leido.texto, titulo)));
    }
  }
  return agregarMateriales([], leidos).materiales;
}

/* ── Lo que se le dice al usuario ───────────────────────────────────────── */

/** Título y detalle de la tarjeta de un material. Puro para que el texto de
 *  cada tipo se pueda fijar en un test: es donde vive la frase que no puede
 *  mentir ("no se subió"). */
export function describirMaterial(material: MaterialConocimiento): {
  titulo: string;
  detalle: string;
} {
  if (material.tipo === "archivo") {
    return {
      titulo: material.nombre,
      detalle: `${material.formato} · ${formatearTamano(material.bytes)} · el archivo no se subió`,
    };
  }
  if (material.tipo === "web") {
    return { titulo: material.sitio, detalle: material.url.replace(/^https?:\/\/(?:www\.)?/i, "") };
  }
  const caracteres = material.texto.length.toLocaleString("es-AR");
  return { titulo: material.titulo, detalle: `Texto pegado · ${caracteres} caracteres` };
}

/**
 * El aviso que aparece después de guardar. Dice QUÉ quedó y, en el caso de los
 * archivos, que el archivo NO SE SUBIÓ — la frase más importante de la
 * pantalla, y por eso está en una función pura con un test que la fija en vez
 * de suelta en un JSX que nadie mira.
 *
 * Va SIN punto final: quien lo muestra le pega atrás la cola que dice dónde
 * quedó guardado (que depende del estado del navegador y de la marca, no de
 * qué se guardó).
 */
export function mensajeMaterialesAnotados(resumen: ResumenAgregado): string {
  // `resumen.nuevos`, NUNCA lo que se le pidió agregar: si el primero de la
  // tanda ya estaba en la lista, nombrarlo diría que se anotó el único que no.
  const primero = resumen.nuevos[0];
  // Sin nada agregado no hay aviso que dar: quien llama muestra el rechazo
  // ("ya está en la lista") y no cierra el modal. La rama existe para no
  // devolver una frase a medias si algún día se llama igual.
  if (!primero || resumen.agregados === 0) return "No agregamos nada: ya estaba todo en la lista";
  const repetidos =
    resumen.duplicados > 0
      ? ` (${resumen.duplicados === 1 ? "otro ya estaba" : `otros ${resumen.duplicados} ya estaban`})`
      : "";
  if (primero.tipo === "archivo") {
    return resumen.agregados === 1
      ? `Anotamos “${primero.nombre}”${repetidos}. El archivo NO se subió: guardamos sólo el nombre, el tamaño y el tipo`
      : `Anotamos ${resumen.agregados} archivos${repetidos}. Los archivos NO se subieron: guardamos sólo el nombre, el tamaño y el tipo`;
  }
  if (primero.tipo === "web") return `Guardamos el link de ${primero.sitio}`;
  return `Guardamos el texto “${primero.titulo}”`;
}
